<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import draggable from 'vuedraggable';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import BoardHeader from '../components/BoardHeader.vue';
import BoardColumn from '../components/BoardColumn.vue';
import ColumnSkeleton from '../components/ColumnSkeleton.vue';
import StageDialog from '../components/StageDialog.vue';
import DeleteStageDialog from '../components/DeleteStageDialog.vue';
import { useKanbanFilters } from '../composables/useKanbanFilters';
import { useKanbanCardActions } from '../composables/useKanbanCardActions';
import {
  matchesKanbanFilters,
  sortByUrgency,
} from '../helpers/kanbanHelpers';

const store = useStore();
const { t } = useI18n();

const {
  agents,
  inboxes,
  currentUserId,
  filters,
  sortOrder,
  hasActiveFilters,
  resetFilters,
  toggleSort,
} = useKanbanFilters();

const { openConversation, selfAssign, resolve, openContact } =
  useKanbanCardActions();

const stages = useMapGetter('kanbanStages/getStages');
const uiFlags = useMapGetter('kanbanStages/getUIFlags');
const allConversations = useMapGetter('getAllConversations');

const stageDialogRef = ref(null);
const deleteDialogRef = ref(null);

// cópia local gravável para arrastar as COLUNAS
const orderedStages = ref([]);
const isReorderingColumns = ref(false);

watch(
  stages,
  value => {
    // não sobrescreve a ordem local enquanto o usuário está arrastando
    if (isReorderingColumns.value) return;
    orderedStages.value = [...value];
  },
  { immediate: true, deep: true }
);

const isLoading = computed(() => uiFlags.value.isFetching);

const visibleConversations = computed(() =>
  (allConversations.value || []).filter(conversation =>
    matchesKanbanFilters(conversation, filters, currentUserId.value)
  )
);

// mapa estágio -> cards; a PRIMEIRA coluna também recebe as conversas sem estágio
const cardsByStage = computed(() => {
  const buckets = new Map(orderedStages.value.map(stage => [stage.id, []]));
  const unstaged = [];

  visibleConversations.value.forEach(conversation => {
    const bucket = buckets.get(conversation.kanban_stage_id);
    if (bucket) bucket.push(conversation);
    else unstaged.push(conversation);
  });

  const firstStageId = orderedStages.value[0]?.id;
  if (firstStageId) {
    buckets.set(firstStageId, [...unstaged, ...buckets.get(firstStageId)]);
  }

  const result = {};
  buckets.forEach((list, stageId) => {
    result[stageId] = sortByUrgency(list, sortOrder.value);
  });
  return result;
});

const totalCount = computed(() => visibleConversations.value.length);

// Guarda o filtro da lista de conversas para restaurar ao sair do board,
// senão a Caixa de Entrada herda os filtros do Kanban.
const previousChatListFilters = ref(null);

onMounted(() => {
  store
    .dispatch('kanbanStages/get')
    .catch(() => useAlert(t('KANBAN.STAGE.API_ERROR')));

  previousChatListFilters.value = { ...(store.getters.getChatListFilters || {}) };

  // ATENÇÃO: é `setChatListFilters` (escreve conversationFilters, que é o que
  // fetchAllConversations lê). `setConversationFilters` escreve appliedFilters,
  // que é um ARRAY de filtros avançados — passar objeto ali corrompe o store
  // e quebra a ChatList.
  store.dispatch('setChatListFilters', {
    status: 'open',
    assigneeType: 'all',
    page: 1,
  });
  store.dispatch('fetchAllConversations');
});

onBeforeUnmount(() => {
  if (previousChatListFilters.value) {
    store.dispatch('setChatListFilters', previousChatListFilters.value);
  }
});

/* ---------- colunas ---------- */
const onAddStage = () => stageDialogRef.value.open();

const onStageAction = ({ action, stage }) => {
  if (action === 'rename' || action === 'color') {
    stageDialogRef.value.open(stage);
  }
  if (action === 'delete') deleteDialogRef.value.open(stage);
};

const onStageSubmit = async ({ id, name, color }) => {
  stageDialogRef.value.setSaving(true);
  try {
    if (id) {
      await store.dispatch('kanbanStages/update', { id, name, color });
    } else {
      await store.dispatch('kanbanStages/create', {
        name,
        color,
        position: orderedStages.value.length + 1,
      });
    }
    stageDialogRef.value.close();
    useAlert(
      t(id ? 'KANBAN.STAGE.EDIT.SUCCESS' : 'KANBAN.STAGE.ADD.SUCCESS')
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.message || t('KANBAN.STAGE.API_ERROR')
    );
  } finally {
    stageDialogRef.value.setSaving(false);
  }
};

const onStageDelete = async stage => {
  deleteDialogRef.value.setDeleting(true);
  try {
    await store.dispatch('kanbanStages/delete', stage.id);
    deleteDialogRef.value.close();
    useAlert(t('KANBAN.STAGE.DELETE.SUCCESS'));
  } catch (error) {
    useAlert(t('KANBAN.STAGE.API_ERROR'));
  } finally {
    deleteDialogRef.value.setDeleting(false);
  }
};

// O Sortable dispara @end mesmo em clique sem arrasto — só persiste se a ordem
// realmente mudou, senão cada clique no cabeçalho viraria um POST.
const persistStageOrder = async event => {
  isReorderingColumns.value = false;

  const { oldIndex, newIndex } = event || {};
  if (
    typeof oldIndex === 'number' &&
    typeof newIndex === 'number' &&
    oldIndex === newIndex
  ) {
    return;
  }

  const positions = orderedStages.value.map((stage, index) => ({
    id: stage.id,
    position: index + 1,
  }));
  try {
    await store.dispatch('kanbanStages/reorder', positions);
  } catch (error) {
    useAlert(t('KANBAN.STAGE.REORDER_ERROR'));
  }
};

/* ---------- cards ---------- */
const onMove = async ({ conversation, stageId }) => {
  try {
    await store.dispatch('moveConversationToStage', {
      conversationId: conversation.id,
      kanbanStageId: stageId,
    });
  } catch (error) {
    useAlert(t('KANBAN.CARD.MOVE_ERROR'));
  }
};

const onCardAction = ({ action, conversation }) => {
  if (action === 'open') openConversation(conversation);
  if (action === 'self-assign') selfAssign(conversation);
  if (action === 'resolve') resolve(conversation);
  if (action === 'contact') openContact(conversation);
};
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <BoardHeader
      :filters="filters"
      :sort-order="sortOrder"
      :agents="agents"
      :inboxes="inboxes"
      :total-count="totalCount"
      :has-active-filters="hasActiveFilters"
      @add-stage="onAddStage"
      @toggle-sort="toggleSort"
      @reset="resetFilters"
      @update:filter="({ key, value }) => (filters[key] = value)"
    />

    <div
      v-if="isLoading && !orderedStages.length"
      class="flex flex-row items-stretch flex-1 gap-4 px-6 py-4 overflow-hidden min-h-0"
    >
      <ColumnSkeleton v-for="n in 4" :key="n" />
    </div>

    <draggable
      v-else-if="orderedStages.length"
      v-model="orderedStages"
      item-key="id"
      handle=".kanban-column-handle"
      :animation="200"
      ghost-class="kanban-column-ghost"
      class="group/board flex flex-row items-stretch flex-1 gap-4 px-6 py-4 overflow-x-auto min-h-0"
      @start="isReorderingColumns = true"
      @end="persistStageOrder"
    >
      <template #item="{ element: stage }">
        <BoardColumn
          :stage="stage"
          :cards="cardsByStage[stage.id] || []"
          @move="onMove"
          @card-action="onCardAction"
          @card-click="openConversation"
          @stage-action="onStageAction"
        />
      </template>
    </draggable>

    <div v-else class="flex flex-col items-center gap-3 m-auto">
      <p class="text-sm text-n-slate-11">{{ t('KANBAN.EMPTY_STAGES') }}</p>
    </div>

    <StageDialog ref="stageDialogRef" @submit="onStageSubmit" />
    <DeleteStageDialog ref="deleteDialogRef" @confirm="onStageDelete" />
  </div>
</template>

<style scoped>
.kanban-column-ghost {
  @apply opacity-50 outline-dashed outline-2 outline-n-brand;
}
</style>
